import { useEffect, useState, useRef } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { Table, Form, Pagination, Dropdown, InputGroup, Button } from "react-bootstrap";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getBondsDataService } from "../../services/BondsServices";
import moment from "moment/moment";
import {
  exportTableDataAsCSV,
  exportTableAsPDF,
} from "../../utils/systemUtils";
import {
  faFileDownload,
  faSort,
  faSearch,
  faPrint,
  faPlusCircle,
  faClock,
  faTimesCircle,
  faEye,
  faCheckCircle,
  faShieldAlt
} from "@fortawesome/free-solid-svg-icons";
import { Settings } from "react-feather";
import { BOND_STATUS_LABEL, ROLES } from "../../types";
import { useOktaAuth } from "@okta/okta-react";

const MacUserLandingPage = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const bondsDaTableRef = useRef(null);
  const fullHiddenBondsDaTableRef = useRef(null);
  const user = useSelector((state) => state?.user);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [bondsData, setBondsData] = useState([]);
  const [error, setError] = useState("");
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(15);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [sortDirection, setSortDirection] = useState("ascending");
  const [currentData, setCurrentData] = useState([]);
  const searchEnableRef = useRef(false);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;

  const USDollar = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        let bonds_respo = await getBondsDataService(user.userinfo.email, "R");
        bonds_respo = bonds_respo.map((bond) => {
                let formatted_date = ''
                if (bond.closing_date) {
                  let parsedDate = moment(bond.closing_date, ['DD-MM-YYYY', moment.ISO_8601, 'MMMM D YYYY'], true);
                  if (!parsedDate.isValid()) {
                    parsedDate = moment(new Date(bond.closing_date))
                  }
                  if (parsedDate.isValid()) {
                    formatted_date = parsedDate.format("MM/DD/YYYY")
                  }
        
                }
        
                return {
                  ...bond,
                  closing_date: formatted_date
                }
        
              })
        console.log(bonds_respo);
        setBondsData(bonds_respo);
        setError("");
      } catch (error) {
        console.log(error)
        setError("Error fetching Bonds");
      }
    };
    fetchData();
  }, [user]);

  useEffect(() => {
    if (authState?.isAuthenticated) {
      setIsLoggedIn(true)
    }
  }, [authState]);

  useEffect(() => {
    const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
    console.log(currentItems)
    setCurrentData(currentItems);
  }, [filteredData, currentPage, indexOfFirstItem, indexOfLastItem]);

  useEffect(() => {
    if (!bondsData || bondsData.length === 0) return;

    // Reset when search is cleared
    if (!searchTerm || searchTerm.trim().length === 0) {
      setFilteredData(bondsData);
      return;
    }

    const term = searchTerm.toLowerCase().trim();

    const filtered = filteredData.filter((bond) => {
      // Safe string helper
      const safe = (val) =>
        val ? val.toString().toLowerCase() : '';

      const lawFirm = safe(bond.law_firm);
      const aagName = safe(bond.aag_name);
      const issuer = safe(bond.Issuer);
      const title = safe(bond.title);
      const bondNumber = safe(bond.bond_issue_number);
      const bondStatus = safe(bond.bond_status);
      const parDollar = safe(bond.par_dollar_amount);
      const opinionRequestedBy = safe(bond.opinion_requested_by);

      // ✅ UPDATED: use raw string instead of normalized date
      const closingDate = safe(bond.closing_date);
      // Search inside nested array safely
      const matchAagUser =
        Array.isArray(bond.aag_userinfo) &&
        bond.aag_userinfo.some((person) =>
          safe(person?.FirstName).includes(term)
        );

      // TEXT MATCH
      const textMatch =
        lawFirm.includes(term) ||
        aagName.includes(term) ||
        issuer.includes(term) ||
        title.includes(term) ||
        bondNumber.includes(term) ||
        bondStatus.includes(term) ||
        parDollar.includes(term) ||
        opinionRequestedBy.includes(term);

      // ✅ UPDATED: simple string match for date
      const dateMatch =
        closingDate.includes(term);

      return textMatch || dateMatch || matchAagUser;
    });

    setFilteredData(filtered);

    // Optional: trigger filter state once
    // if (!searchEnableRef.current) {
    //   onChangeFilter('my_transcripts');
    //   searchEnableRef.current = true;
    // }
  }, [bondsData, searchTerm]);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1);
  };

  const handleSorting = (key) => {
    const sortedItems = [...filteredData].sort((a, b) => {
      let itemA = a[key];
      let itemB = b[key];

      if (key === "par_dollar_amount") {
        itemA = parseFloat(itemA);
        itemB = parseFloat(itemB);
      }
      else if (key === 'closing_date') {
        const parseDate = (dateStr) => {
          if (!dateStr) return 0;
          const [month, day, year] = dateStr.split('/');
          return new Date(year, month - 1, day).getTime();
        };

        itemA = parseDate(itemA);
        itemB = parseDate(itemB);
      }
      else {
        itemA = itemA?.toString().toUpperCase();
        itemB = itemB?.toString().toUpperCase();
      }

      if (itemA < itemB) {
        return sortDirection === "ascending" ? -1 : 1;
      }
      if (itemA > itemB) {
        return sortDirection === "ascending" ? 1 : -1;
      }
      return 0;
    });

    setFilteredData(sortedItems);
    setSortDirection(
      sortDirection === "ascending" ? "descending" : "ascending"
    );
  };


  if (!isLoggedIn) {
    return navigate("/");
  }

  const statusIcons = {
    "New": <FontAwesomeIcon className="status-icon green" icon={faPlusCircle}></FontAwesomeIcon>,
    "Registered": <FontAwesomeIcon className="status-icon emerald-green" icon={faCheckCircle}></FontAwesomeIcon>,
    "Delayed": <FontAwesomeIcon className="status-icon amber" icon={faClock}></FontAwesomeIcon>,
    "Cancelled": <FontAwesomeIcon className="status-icon red" icon={faTimesCircle}></FontAwesomeIcon>,
    "Opinion Issued": <FontAwesomeIcon className="status-icon indigo" icon={faShieldAlt}></FontAwesomeIcon>,
    "Under Review": <FontAwesomeIcon className="status-icon blue" icon={faEye}></FontAwesomeIcon>

  }


  return (
    <div className="container-fluid">
      <div className="col-md-12">
        <div className="row">
          <div className="col-12">
            <div className="dashboard-header">
              <h1>MAC Landing Page</h1>
            </div>
            {error && (
              <div className="alert alert-warning">
                <strong>Warning!</strong> {error}
              </div>
            )}
          </div>
        </div>
        <div className="table-container">
          <div className="child-div">
            <div style={{ width: "95%" }}>
              <div className="row search-print-container">
                <div className="col-md-3">
                  <InputGroup>
                    <InputGroup.Text>
                      <FontAwesomeIcon icon={faSearch}></FontAwesomeIcon>
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder="Search"
                      value={searchTerm}
                      onChange={handleSearch}
                    />
                  </InputGroup>
                </div>
                <div className="col-md-6"></div>
                <div className="col-md-3 export-container">
                  <Button
                    className="btn-download"
                    variant="primary"
                    onClick={(event) => {
                      event.stopPropagation();
                      exportTableDataAsCSV(
                        fullHiddenBondsDaTableRef,
                        ROLES.MAC_USER
                      );
                    }}
                  >
                    <FontAwesomeIcon icon={faFileDownload} /> Download
                  </Button>
                  <Button
                    variant="primary"
                    onClick={(event) => {
                      event.stopPropagation();
                      exportTableAsPDF(fullHiddenBondsDaTableRef, "BCU");
                    }}
                  >
                    <FontAwesomeIcon icon={faPrint} /> Print
                  </Button>

                </div>
              </div>
              <Table
                ref={bondsDaTableRef}
              >
                <thead>
                  <tr style={{ whiteSpace: "nowrap", fontSize: "0.9em" }}>
                    <th onClick={() => handleSorting("bond_issue_number")}>
                      Issue/Opinion #{" "}
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th onClick={() => handleSorting("closing_date")}>
                      Closing Date
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th onClick={() => handleSorting("Issuer")}>
                      Issuer{" "}
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th onClick={() => handleSorting("title")}>
                      Title{" "}
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th onClick={() => handleSorting("par_dollar_amount")}>
                      Par Dollar Amount{" "}
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                    <th onClick={() => handleSorting("bond_status")}>
                      Status
                      <span>
                        <FontAwesomeIcon icon={faSort} className="ms-2" />
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {currentData.map((bond) => (
                    <tr key={bond.id}>
                      <td>
                        {bond.bond_issue_number && bond.bond_status !==
                          BOND_STATUS_LABEL.RegistrationComplete ? (
                          <span>{bond.bond_issue_number}</span>
                        ) : (
                          bond.bond_issue_number && (
                            <a
                              href={`/bond-details/${bond.bond_issue_number}/edit`}
                              target="_self"
                              rel="noopener noreferrer"
                            >
                              {bond.bond_issue_number}
                            </a>
                          )
                        )}
                      </td>
                      <td>{bond.closing_date}</td>
                      <td>{bond.Issuer}</td>
                      <td>{bond.title}</td>
                      <td>{USDollar.format(bond.par_dollar_amount)}</td>
                      <td className="no-resize">{statusIcons[bond.bond_status]} {bond.bond_status}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>

              {/* this table is hidden  */}
              <div style={{ display: "none" }}>
                <Table
                  responsive
                  bordered
                  striped
                  hover
                  size="sm"
                  ref={fullHiddenBondsDaTableRef}
                >
                  <thead>
                    <tr style={{ whiteSpace: "nowrap", fontSize: "0.9em" }}>
                      <th>
                        Issue/Opinion #{" "}
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Closing Date
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Issuer{" "}
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Title{" "}
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Par Dollar Amount{" "}
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                      <th>
                        Status
                        <span>
                          <FontAwesomeIcon icon={faSort} className="ms-2" />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.map((bond) => (
                      <tr key={bond.id}>
                        <td>
                          {bond.bond_issue_number && bond.bond_status !==
                            BOND_STATUS_LABEL.RegistrationComplete ? (
                            <span>{bond.bond_issue_number}</span>
                          ) : (
                            bond.bond_issue_number && (
                              <a
                                href={`/bond-details/${bond.bond_issue_number}/edit`}
                                target="_self"
                                rel="noopener noreferrer"
                              >
                                {bond.bond_issue_number}
                              </a>
                            )
                          )}
                        </td>
                        <td>{bond.closing_date}</td>
                        <td>{bond.Issuer}</td>
                        <td>{bond.title}</td>
                        <td>{USDollar.format(bond.par_dollar_amount)}</td>
                        <td>{bond.bond_status}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
              <div className="d-flex justify-content-end">
                <Pagination className="float-end">
                  <Pagination.First
                    onClick={() => paginate(1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Prev
                    onClick={() => paginate(currentPage - 1)}
                    disabled={currentPage === 1}
                  />
                  <Pagination.Item>
                    Page {currentPage} of {Math.ceil(filteredData.length / itemsPerPage)}
                  </Pagination.Item>
                  <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
                  />
                  <Pagination.Last
                    onClick={() => paginate(Math.ceil(filteredData.length / itemsPerPage))}
                    disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
                  />
                </Pagination>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MacUserLandingPage;
